import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, DollarSign, Users, TrendingDown, Activity, Plus, Tag, Ban, ClipboardCheck, Check, ScrollText, Rocket } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="mb-14">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold uppercase tracking-tight text-2xl flex items-center gap-2">
          {Icon && <Icon size={20} className="text-white/70" />} {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function BillingTab() {
  const [rev, setRev] = useState(null);
  const [promos, setPromos] = useState([]);
  const [audit, setAudit] = useState([]);
  const [launch, setLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPromo, setShowPromo] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyPromo = { code: "", percent_off: "", amount_off: "", duration: "once" };
  const [promoForm, setPromoForm] = useState(emptyPromo);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, a, l] = await Promise.all([
        api.get("/admin/revenue"), api.get("/admin/promo-codes"),
        api.get("/admin/audit-logs"), api.get("/admin/launch"),
      ]);
      setRev(r.data); setPromos(p.data.promo_codes || []); setAudit(a.data); setLaunch(l.data);
    } catch {
      toast.error("Failed to load billing dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPromo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { code: promoForm.code, duration: promoForm.duration };
      if (promoForm.percent_off) body.percent_off = parseFloat(promoForm.percent_off);
      else if (promoForm.amount_off) body.amount_off = parseFloat(promoForm.amount_off);
      await api.post("/admin/promo-codes", body);
      toast.success("Promo code created.");
      setPromoForm(emptyPromo); setShowPromo(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deactivatePromo = async (id) => {
    try {
      await api.post(`/admin/promo-codes/${id}/deactivate`);
      setPromos((prev) => prev.map((p) => (p.id === id ? { ...p, active: false } : p)));
      toast.success("Promo deactivated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCheck = async (key, done) => {
    try {
      await api.patch("/admin/launch", { key, done });
      setLaunch((prev) => ({ ...prev, checklist: prev.checklist.map((c) => (c.key === key ? { ...c, done } : c)) }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="billing-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">Billing & Revenue</h1>

      {rev && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12">
          {[
            { icon: DollarSign, label: "MRR", value: `$${rev.mrr.toLocaleString()}` },
            { icon: TrendingDown, label: "ARR", value: `$${rev.arr.toLocaleString()}` },
            { icon: Users, label: "Active Members", value: rev.active_members },
            { icon: Activity, label: "Churn Rate", value: `${rev.churn_rate}%` },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={`rev-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={18} className="text-white/70 mb-3" />
                <p className="font-mono-accent text-3xl font-bold">{s.value}</p>
                <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {rev && Object.keys(rev.by_plan).length > 0 && (
        <Section title="Revenue by Plan" icon={DollarSign}>
          <div className="border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid="revenue-by-plan">
            {Object.entries(rev.by_plan).map(([name, v]) => (
              <div key={name} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm">{name}</span>
                <span className="text-sm text-white/60">{v.count} members · <span className="font-mono-accent text-white">${v.mrr.toLocaleString()}/mo</span></span>
              </div>
            ))}
            <div className="flex items-center justify-between px-5 py-3 text-white/50 text-xs uppercase tracking-wide">
              <span>Total collected (all-time)</span>
              <span className="font-mono-accent text-white text-sm">${rev.total_collected.toLocaleString()}</span>
            </div>
          </div>
        </Section>
      )}

      <Section
        title="Promo Codes"
        icon={Tag}
        action={
          <button data-testid="toggle-add-promo" onClick={() => setShowPromo((v) => !v)}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">
            <Plus size={14} /> {showPromo ? "Cancel" : "New Code"}
          </button>
        }
      >
        {showPromo && (
          <form onSubmit={createPromo} className="bg-[#121212] border border-white/10 p-6 mb-6 grid sm:grid-cols-4 gap-4" data-testid="promo-form">
            <div>
              <label className={labelCls}>Code</label>
              <input data-testid="promo-code" required value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} className={inputCls} placeholder="FOUNDING20" />
            </div>
            <div>
              <label className={labelCls}>% Off</label>
              <input data-testid="promo-percent" type="number" min="1" max="100" value={promoForm.percent_off} onChange={(e) => setPromoForm({ ...promoForm, percent_off: e.target.value, amount_off: "" })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>$ Off (or)</label>
              <input data-testid="promo-amount" type="number" min="1" step="0.01" value={promoForm.amount_off} onChange={(e) => setPromoForm({ ...promoForm, amount_off: e.target.value, percent_off: "" })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Duration</label>
              <select data-testid="promo-duration" value={promoForm.duration} onChange={(e) => setPromoForm({ ...promoForm, duration: e.target.value })} className={inputCls}>
                {["once", "forever", "repeating"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="sm:col-span-4">
              <button data-testid="promo-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Code
              </button>
            </div>
          </form>
        )}
        {promos.length === 0 ? (
          <p className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="promos-empty">No promo codes yet. Customers enter codes on the Stripe checkout page.</p>
        ) : (
          <div className="border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid="promos-list">
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-${p.id}`} className="flex items-center justify-between px-5 py-4 gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono-accent font-bold tracking-widest">{p.code}</span>
                  <span className="text-white/60 text-sm">{p.percent_off ? `${p.percent_off}% off` : p.amount_off ? `$${p.amount_off} off` : ""} · {p.duration}</span>
                  {!p.active && <span className="text-white/30 text-[10px] uppercase tracking-widest border border-white/15 px-2 py-0.5">Inactive</span>}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white/40 text-xs">{p.times_redeemed} used{p.max_redemptions ? `/${p.max_redemptions}` : ""}</span>
                  {p.active && (
                    <button data-testid={`promo-deactivate-${p.id}`} onClick={() => deactivatePromo(p.id)} className="text-white/40 hover:text-white transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
                      <Ban size={13} /> Disable
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {launch && (
        <Section title="Launch Checklist" icon={Rocket}>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-5 text-sm text-white/50" data-testid="launch-stats">
            <span>Mode: <span className="font-mono-accent uppercase text-white">{launch.stats.stripe_mode}</span></span>
            <span>Paid txns: <span className="text-white">{launch.stats.paid_transactions}</span></span>
            <span>Webhooks processed: <span className="text-white">{launch.stats.webhook_events_processed}</span></span>
            <span>Active memberships: <span className="text-white">{launch.stats.active_memberships}</span></span>
          </div>
          <div className="border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid="launch-checklist">
            {launch.checklist.map((c) => (
              <button
                key={c.key}
                data-testid={`launch-check-${c.key}`}
                onClick={() => toggleCheck(c.key, !c.done)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-white/[0.03] transition-colors"
              >
                <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${c.done ? "bg-white border-white" : "border-white/30"}`}>
                  {c.done && <Check size={13} className="text-black" />}
                </span>
                <span className={`text-sm ${c.done ? "text-white/40 line-through" : "text-white/80"}`}>{c.label}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      <Section title="Audit Log" icon={ScrollText}>
        {audit.length === 0 ? (
          <p className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="audit-empty">No billing actions logged yet.</p>
        ) : (
          <div className="border border-white/10 bg-[#121212] divide-y divide-white/5 max-h-96 overflow-y-auto" data-testid="audit-list">
            {audit.map((l) => (
              <div key={l.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <ClipboardCheck size={14} className="text-white/40" />
                  <span className="font-mono-accent text-xs uppercase tracking-widest text-white/70">{l.action}</span>
                  <span className="text-white/50">{l.detail}</span>
                </div>
                <span className="text-white/30 text-xs shrink-0">{new Date(l.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
